<template lang="pug">
.separate-file-list-modal
  b-modal(v-model="_value" size="xl" @ok="ok" @cancel="cancel" no-close-on-backdrop no-close-on-esc)
    .separate-fileselect-body
      SeparateFileTable(
        v-model="items"
        :fields="fields"
        :totalRows.sync="rows"
        :currentPage="currentPage"
        :perPage="perPage"
        :isLoading="isLoading"
        :filter="filter"
        :filterFields="filterFields"
      )
      .table-options.d-flex.flex-row.justify-content-end.align-items-center
        // フィルタリング
        span.table-option-text 利用者グループ:
        .filter
          b-form-select(id="filter-input" v-model="filter" :options="filterOptions" size="md")
        // ページあたりの行数
        span.table-option-text 1ページの行数:
        .per-page
          b-form-select(id="per-page-select" v-model="perPage" :options="pageOptions" size="md" @change="selectPerPage")
        // ページネーション
        b-pagination.m-0(v-model="currentPage" :total-rows="rows" :per-page="perPage" :disabled="isLoading" @change="selectPage" hide-goto-end-buttons)

    template(#modal-footer="{ ok, cancel }")
      b-button.btn.modal-ok-btn(@click="ok") OK
      b-button.btn.modal-cancel-btn(@click="cancel") キャンセル
</template>

<script lang="ts">
import { Component, Prop, Inject, Watch, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";

import Setting from "../../../../public/setting.json";

import ResidentsService from "@/commons/services/Residents";
import ResidentGroupsService, { ResidentGroupsGetModel } from "@/commons/services/ResidentGroups";

import SeparateFileTable, { SeparateFileTableField, SeparateFileTableItem } from "@/pc/containers/sendDocuments/SeparateFileTable.vue";
import { SendTarget } from "@/pc/components/forms/FormSendTarget.vue";
import { ResidentSeparateFiles } from "@/pc/pages/sendDocuments/SendDocumentRegister.vue";

/**
 * ### 文書送付、「利用者毎に異なる文書」のファイルリストモーダル
 */
@Component({
  components: {
    SeparateFileTable
  }
})
export default class SeparateFileListModal extends Vue {
  /** 利用者サービス */
  @Inject("ResidentsService") residentsService!: ResidentsService;
  /** 利用者グループサービス */
  @Inject("ResidentGroupsService") residentGroupsService!: ResidentGroupsService;

  /** 表示可否 */
  @Prop({ required: true })
  value!: boolean;
  /** テーブル行情報 */
  @Prop({ required: true })
  items!: Array<SeparateFileTableItem>;
  /** ページングの全体行数 */
  @Prop()
  rows!: number;
  /** 読込み中か否か */
  @Prop()
  isLoading!: boolean;

  /** テーブル列情報 */
  fields: Array<SeparateFileTableField> = [];
  /** フィルタリング条件 */
  filter: any = "";
  /** フィルター選択項目 */
  filterOptions: Array<{ value: string; text: string }> = [];
  /** フィルタリング対象カラム名 */
  filterFields: string[] = ["groupId"];
  /** ページングの現在ページ番号 */
  currentPage: number = 1;
  /** 1ページに表示する行数 */
  perPage: number = Number(Setting.sendDocumentRegister.perPage);
  /** 1ページに表示する行数の選択項目 */
  pageOptions: number[] = Setting.sendDocumentRegister.pageOptions;

  get _value(): boolean {
    return this.value;
  }

  set _value(newValue: boolean) {
    this.$emit("input", newValue);
    return;
  }

  /**
   * ### 1ページあたりの表示行数の変更イベント
   *
   * - 変更をクッキーに持たせる
   */
  @Watch("perPage")
  public changePerPage(perPage: number): void {
    this.setCookie(perPage);
    return;
  }

  /**
   * ### 1ページあたりの表示行数をCookieに設定する
   */
  public setCookie(perPage: number): void {
    // Cookie有効秒数。有効期限は必ず設定しないといけないので、10年分にしておく
    const saveSecond = 10 * 365 * 24 * 60 * 60;
    document.cookie = `send-document-register-per-page=${perPage}; max-age=${saveSecond};`;
    return;
  }

  /**
   * vue lifecycle mounted
   *
   * 画面表示情報の作成を行う
   */
  public async mounted(): Promise<void> {
    // 画面初期表示時に作成して変更しないものを初期化する
    const fields: Array<SeparateFileTableField> = this.createFields();
    this.fields = fields;
    // 1ページあたりの表示行数はCookieから取得する。取得できない場合はデフォルト値を使用する
    const perPage: number | null = this.loadCookie();
    this.perPage = perPage ? perPage : Number(Setting.resident.perPage);

    // グループIDとグループ名の変換用にグループ情報のMAPを作る
    const groups: Array<ResidentGroupsGetModel> = await this.getResidentGroups();
    const filterOptions: Array<{ value: string; text: string }> = await this.createFilterOptions(groups);
    this.filterOptions = filterOptions;

    return;
  }

  /**
   * ### 列名を作成する
   */
  public createFields(): Array<SeparateFileTableField> {
    const fields: Array<SeparateFileTableField> = [];
    fields.push({ key: "group", label: "利用者グループ", sortable: false, sortDirection: "desc", class: "group" });
    fields.push({ key: "targets", label: "宛先家族名（続柄）", sortable: false, sortDirection: "desc", class: "targets" });
    fields.push({ key: "files", label: "登録ファイル", sortable: false, sortDirection: "desc", class: "file-names" });
    fields.push({ key: "controll", label: "操作", sortable: false, sortDirection: "desc", class: "controll text-center" });
    return fields;
  }

  /**
   * ### Cookieを見て、値が保持されている場合はCookieの値を返す
   */
  public loadCookie(): number | null {
    if (document.cookie) {
      let cookieMap: any = {};
      let cookies: Array<string> = document.cookie.split("; ");
      cookies.forEach(function (value) {
        //cookie名と値に分ける
        let content = value.split("=");
        cookieMap[content[0]] = content[1];
      });

      // cookieに値が格納されていないとundefinedが格納されているが、実際にはゴミデータが格納されているのでisNaN()でチェックが必要
      const perPage: number = cookieMap["send-document-register-per-page"];

      return isNaN(perPage) ? null : perPage;
    }
    return null;
  }

  /**
   * ### 全ての利用者グループを取得する
   */
  public async getResidentGroups(): Promise<Array<ResidentGroupsGetModel>> {
    const response: Array<ResidentGroupsGetModel> = await this.residentGroupsService.gets(userStore);
    return response;
  }

  /**
   * ### 利用者グループフィルター用選択項目
   *
   * - 検索用に利用者グループマスターのMAPを作る
   * - アカウント情報から許可されている利用者グループIDの配列を取得する
   * - 利用者グループマスターと照合して選択項目の利用者グループを作成する
   */
  public createFilterOptions(residentGroups: Array<ResidentGroupsGetModel>): Array<{ value: string; text: string }> {
    const filterOptions: Array<{ value: string; text: string }> = [];
    filterOptions.push({
      value: "",
      text: "全て"
    });

    const allowedGroupsMap: Map<number, number> = new Map<number, number>();
    for (const allowedGroup of userStore.loginUser.permissions.residentGroups) {
      allowedGroupsMap.set(allowedGroup, allowedGroup);
    }
    for (const group of residentGroups) {
      if (allowedGroupsMap.has(group.id)) {
        filterOptions.push({
          value: `${group.id}`,
          text: group.name
        });
      }
    }
    return filterOptions;
  }

  /**
   * ### 1ページあたりの表示行数変更イベント
   *
   * - 1ページあたりの表示行数を更新
   * - ページあたりの表示行数が変わるので表の情報を作り直す
   */
  public async selectPerPage(perPage: number): Promise<void> {
    this.perPage = perPage;
    this.currentPage = 1;
    return;
  }

  /**
   * ### ページネーションのページ番号押下
   */
  public async selectPage(page: number): Promise<void> {
    this.currentPage = page;
    return;
  }

  public async ok(): Promise<void> {
    const files: Array<ResidentSeparateFiles> = [];
    for (const item of this.items) {
      if (0 < item.files.length) {
        for (const family of item.targetFamilies) {
          files.push({
            sortKey: family.key,
            files: item.files
          });
        }
      }
    }
    this.$emit("ok", files);
    return;
  }

  public cancel(): void {
    this.$emit("cancel");
    return;
  }
}
export interface SendTargetExtFiles extends SendTarget {
  files: Array<File>;
}
</script>
